import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { Pwerm2WaterfallPartialTableProps } from '../pwerm-waterfall-utils/pwerm2-waterfall-shared';
import { FC, Fragment } from 'react';
import { useLocale } from '@app/core/hooks/useLocale';
import { enumKeyByValue } from '@app/shared/helpers';
import { WaterfallSectionsErf } from '@app/shared/models/contracts/enums/shared-enums';
import classNames from 'classnames';
import useTogglerPwermWaterfall from '@app/core/hooks/useTogglerPwermWaterfall';
import { WaterfallTableExpandableRow } from '@app/modules/projects/outputs/pwerm2-summary-waterfall/waterfall-table/WaterfallTableExpandableRow';
import { selectInstruments } from '@app/core/store/capital-structure-slice-selectors';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import { CashFlowMovementValuationSection } from './CashFlowMovementValuation';
import { getCashFlowFlagsForInstrument } from '@app/core/store/pwerm2-calculation-slice-selectors';

export const Pwerm2WaterfallInterimCashFlowMovementByInstrument: FC<
  Pwerm2WaterfallPartialTableProps
> = ({ columnsCount }) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const instruments = useInstrumentSort(useAppSelector(selectInstruments ?? []));
  const { l } = useLocale();
  const pwermWaterfallTableStates = useAppSelector(
    (state) => state.uiValues.pwermWaterfallTableExpandStates
  );
  const { handleToggle } = useTogglerPwermWaterfall(WaterfallSectionsErf.InterimCashFlows);

  const filteredInstruments = instruments.filter((instrument) => {
    const flags = getCashFlowFlagsForInstrument(instrument.instrumentId, results);
    return (
      flags.hasInfusions || flags.hasRedemptions || flags.hasCashPayInterest || flags.hasDividends
    );
  });

  if (filteredInstruments.length === 0) {
    return null;
  }

  return (
    <>
      <tbody data-testid="interim-cashflowss-expandable-row">
        <WaterfallTableExpandableRow
          title={l('_InterimCashFlows')}
          id={enumKeyByValue(WaterfallSectionsErf, WaterfallSectionsErf.InterimCashFlows)}
          isOpen={pwermWaterfallTableStates[WaterfallSectionsErf.InterimCashFlows]}
          setIsOpen={handleToggle}
          colCount={columnsCount}
        />
      </tbody>
      <tbody
        className={classNames('table-primary__table-section', {
          'table-primary__table-section--collapsed':
            !pwermWaterfallTableStates[WaterfallSectionsErf.InterimCashFlows],
        })}
        data-testid="interim-cashflows-section">
        {filteredInstruments.map((instrument) => (
          <Fragment key={`valuation-item-instrument-${instrument.instrumentId}`}>
            <tr data-testid={`instrument-header-row-${instrument.instrumentId}`}>
              <th
                className="table-primary__cell--header-quaternary"
                data-testid={`instrument-header-${instrument.instrumentId}`}>
                {instrument.instrumentNarrative}
              </th>
              <th className="table-primary__cell--header-quaternary" colSpan={columnsCount - 1} />
            </tr>
            <CashFlowMovementValuationSection results={results} instrument={instrument} />
          </Fragment>
        ))}
      </tbody>
    </>
  );
};
